import backendApi from '@/axios/backendApi'
import { AxiosError } from 'axios';
import { fetchRecoltes } from './fetch_tables';
import { transformCodeToDate } from '@/functions/utils';
import { filteredCultures } from '@/functions/functions_cultures';
import { computed } from 'vue';
import { useDataStore } from '@/stores/useDataStore';
import { todaycode } from '@/functions/utils';
import { useToast } from 'vue-toastification'

const toast = useToast()

export const createRecolte = async (t: (key: string, params?: Record<string, unknown>) => string) => {
  const dataStore = useDataStore();

  if (!dataStore.newRecolte?.daycode || !dataStore.newRecolte.machine_id || !dataStore.newRecolte.specie_id) {
    dataStore.creationError = t('creationError.fillRequired');
    return;
  }

  if (isDuplicateRecolte(dataStore.newRecolte.daycode, dataStore.newRecolte.specie_id, dataStore.newRecolte.culture_ids)) {
    dataStore.creationError = t('creationError.duplicateData');
    return;
  }

  try {
    await backendApi.post(`/recoltes/`, {
      daycode: dataStore.newRecolte.daycode,
      machine_id: dataStore.newRecolte.machine_id,
      specie_id: dataStore.newRecolte.specie_id,
      culture_ids: dataStore.newRecolte.culture_ids,
      volume: dataStore.newRecolte.volume
    });

    toast.success(t('toast.recolteCreated'));
    dataStore.showCreateRecolte = false;
    await fetchRecoltes(dataStore.filtreAnnee, dataStore.filtreMois); // recharge la liste
  } catch (err) {
    toast.error(t('toast.recolteCreateError'));
    // Vérifier si l'erreur est une AxiosError
    if (err instanceof AxiosError && err.response) {
      // Si le détail du backend contient le message de duplication
      if (err.response.data.detail && err.response.data.detail.includes("Une récolte avec ce daycode, cette espèce et ces PBRs existe déjà")) {
        dataStore.creationError = t('creationError.duplicateDetail');
      } else {
        dataStore.creationError = t('creationError.generic');
      }
    } else {
      dataStore.creationError = t('creationError.unknown');
    }

    console.error(err);
  }
};

export const addBatchToRecolte = async (recolteId: number, batch: any) => {
  const response = await backendApi.post(`/recoltes/${recolteId}/batches`, batch);
  return response.data;
};

export const selectRecolte = (recolte: Recolte) => {
  const dataStore = useDataStore();
  dataStore.selectedRecolteForBatch = recolte;
  dataStore.showCreateRecolte = false;
};

export function getDryEquivalent(batch: { quantity: number; dry_weight: number }): number {
  return (batch.dry_weight * batch.quantity) / 100;
}

export function getFCXQuantity(batch: { quantity: number; dry_weight: number; concentration_fcx: number }): number {
  return getDryEquivalent(batch) * (batch.concentration_fcx / 100);
}

export async function addBatch(t: (key: string, params?: Record<string, unknown>) => string) {
  const dataStore = useDataStore();
  if (!dataStore.selectedRecolteForBatch) return;

  // Déterminer le numéro du batch en fonction du nombre de batches existants
  const nextBatchNumber = dataStore.selectedRecolteForBatch.batches && dataStore.selectedRecolteForBatch.batches.length > 0
    ? dataStore.selectedRecolteForBatch.batches.length + 1  // Incrémenter le numéro du batch
    : 1;  // Premier batch, donc 1

  // Générer le code du batch basé sur le daycode de la récolte et le numéro du batch
  const baseCode = nextBatchNumber === 1
    ? `${dataStore.selectedRecolteForBatch.daycode}`
    : `${dataStore.selectedRecolteForBatch.daycode}-${nextBatchNumber}`;

  const batchCode = dataStore.newBatch.rec ? `${baseCode}-REC` : baseCode;

  // Crée un batch à partir du formulaire local
  const batchToAdd: BatchCreate = {
    ...dataStore.newBatch,
    batch_code: batchCode,
    quantity: dataStore.newBatch.quantity ?? 0,
    recolte: dataStore.selectedRecolteForBatch,
  };

  try {
    // Ajout du batch au backend
    const createdBatch = await addBatchToRecolte(dataStore.selectedRecolteForBatch.id, batchToAdd);

    // Vérification que le backend renvoie le batch avec un ID généré
    if (!createdBatch) {
      throw new Error(t('errors.batchNotCreated'));
    }

    // Une fois le batch créé, on peut l'ajouter localement
    if (!dataStore.selectedRecolteForBatch.batches) {
      dataStore.selectedRecolteForBatch.batches = [];
    }

    // Ajouter le batch créé au tableau de batches
    dataStore.selectedRecolteForBatch.batches.push(createdBatch);

    toast.success(t('toast.batchAdded'))

    // Reset formulaire
    dataStore.newBatch = {
      batch_code: '',
      quantity: null,
      rec: false,
    };

  } catch (error) {
    console.error(t('toast.batchAddError'), ' : ', error);
    toast.error(t('toast.batchAddError'));
  }
}

export const handleCreateRecolte = async (t: (key: string, params?: Record<string, unknown>) => string) => {
  try {
    await createRecolte(t);
  } catch (error) {
    console.error("Erreur lors de la création de la récolte :", error);

  }
};

export function resetNewRecolte() {
  const dataStore = useDataStore();
  dataStore.newRecolte = emptyRecolte();
}

// Fonction pour démarrer la modification d'une récolte
export function editRecolte(recolte: Recolte) {
  const dataStore = useDataStore();
  dataStore.selectedRecolteForEdit = { ...recolte }; // Clone pour ne pas modifier l'original avant validation
  dataStore.showEditRecolte = true;
}

// Fonction pour supprimer une récolte
export async function deleteRecolte(id: number, t: (key: string, params?: Record<string, unknown>) => string) {
  const dataStore = useDataStore();
  try {
    await backendApi.delete(`/recoltes/${id}`);

    // Rafraîchir la liste des récoltes
    await fetchRecoltes(dataStore.filtreAnnee, dataStore.filtreMois);
    dataStore.selectedRecolteForEdit = null;
    toast.success(t('toast.recolteDeleted'));
  } catch (error) {
    console.error(t('toast.recolteDeleteError'), " : ", error);
    toast.error(t('toast.recolteDeleteError'));
  }
}

// Fonction pour supprimer un batch
export async function deleteBatch(id: number, t: (key: string, params?: Record<string, unknown>) => string) {
  const dataStore = useDataStore();
  try {
    await backendApi.delete(`/batches/${id}`);

    // Rafraîchir la liste des batches après suppression
    await fetchRecoltes(dataStore.filtreAnnee, dataStore.filtreMois);
    toast.success(t('toast.batchDeleted'));
  } catch (error) {
    console.error(t('toast.batchDeleteError'), " : ", error);
    toast.error(t('toast.batchDeleteError'));
  }
}

// Fonction de soumission du formulaire d'édition
export async function handleEditSubmit(t: (key: string, params?: Record<string, unknown>) => string) {
  const dataStore = useDataStore();
  try {
    const edited = dataStore.selectedRecolteForEdit;

    const payload = {
      daycode: edited?.daycode,
      volume: edited?.volume,
      machine_id: edited?.machine?.id,
      specie_id: edited?.specie?.id,
      culture_ids: edited?.cultures?.map(culture => culture.id),
    };

    await backendApi.patch(`/recoltes/${edited?.id}`, payload);

    await fetchRecoltes(dataStore.filtreAnnee, dataStore.filtreMois);
    dataStore.showEditRecolte = false;
    dataStore.selectedRecolteForEdit = null;
  } catch (error) {
    console.error(t('toast.recolteUpdateError'), " : ", error);
    toast.error(t('toast.recolteUpdateError'));
  }
}

export function useRecolteComputed() {
  const dataStore = useDataStore();

  const selectedCultureObjects = computed(() =>
    filteredCultures.value.filter(c =>
      dataStore.selectedCultures.includes(c.id)
    )
  );

  const isNoCultureSelected = computed(() =>
    dataStore.selectedCultures.length === 0
  );

  const selectedSpecie = computed(() => {
    const firstSpecie = selectedCultureObjects.value[0]?.specie;
    const allSame = selectedCultureObjects.value.every(
      c => c.specie.id === firstSpecie?.id
    );
    return allSame ? firstSpecie : null;
  });

  const selectedPBRs = computed(() =>
    selectedCultureObjects.value.map(c => c.pbr)
  );

  return {
    selectedCultureObjects,
    isNoCultureSelected,
    selectedSpecie,
    selectedPBRs
  };
}

// Comparaison de deux tableaux comme des ensembles (sans ordre)
function arraysEqualAsSets(a: number[], b: number[]) {
  return a.length === b.length && new Set(a).size === new Set(b).size && a.every(id => b.includes(id));
}

// Vérification du doublon de récolte en fonction des cultures
export function isDuplicateRecolte(daycode: string, specie_id: number, culture_ids: number[]) {
  const dataStore = useDataStore();
  return dataStore.recoltes.some(r => {
    return (
      r.daycode === daycode &&
      r.specie?.id === specie_id &&
      new Set(r.cultures.map(c => c.id)).size === new Set(culture_ids).size &&  // Comparaison des ID des cultures
      arraysEqualAsSets(culture_ids, r.cultures.map(c => c.id))  // Comparaison des ID des cultures
    );
  });
}

export const handleSubmit = async (t: (key: string, params?: Record<string, unknown>) => string) => {
  const dataStore = useDataStore();
  const { selectedSpecie, selectedCultureObjects } = useRecolteComputed();
  dataStore.formErrorMessage = ''; // reset des erreurs

  // Vérification de l'espèce
  if (!selectedSpecie.value) {
    dataStore.formErrorMessage = t('formError.selectAtLeastOneCulture');
    return;
  }

  if (!dataStore.newRecolte) {
    dataStore.newRecolte = emptyRecolte();
  }
  dataStore.newRecolte.specie_id = selectedSpecie.value.id;
  dataStore.newRecolte.culture_ids = selectedCultureObjects.value.map(c => c.id);

  // Vérification du volume
  if (isNaN(dataStore.newRecolte.volume) || dataStore.newRecolte.volume <= 0) {
    dataStore.formErrorMessage = t('formError.volumePositive');
    return;
  }

  // Vérification de la machine
  if (!dataStore.newRecolte.machine_id) {
    dataStore.formErrorMessage = t('formError.selectMachine');
    return;
  }

  // Vérification du jour
  if (!dataStore.newRecolte.daycode) {
    dataStore.formErrorMessage = t('formError.fillDaycode');
    return;
  }

  try {
    // Appel à la création de la récolte
    await handleCreateRecolte(t);
    // Rechargement des récoltes après création
    await fetchRecoltes(dataStore.filtreAnnee, dataStore.filtreMois);
    // Fermeture du formulaire après succès
    dataStore.showCreateRecolte = false;
    // Réinitialisation des champs
    dataStore.newRecolte = emptyRecolte();
  } catch (error: unknown) {
    const err = error as Error;
    dataStore.formErrorMessage = t('formError.submitError', { message: err.message || t('formError.unknownError') });
  }
};

// Fonction de réinitialisation
function emptyRecolte(): {
  daycode: string;
  machine_id: null;
  culture_ids: number[];
  specie_id: null;
  volume: number;
  batches: {
    batch_code: string;
    quantity: number;
    dry_weight: number;
    concentration_fcx: number;
  }[];
} {
  return {
    daycode: todaycode,
    machine_id: null,
    culture_ids: [],
    specie_id: null,
    volume: 0,
    batches: [],
  };
}

export function toggleRecolte(id: number) {
  const dataStore = useDataStore();
  if (dataStore.selectedRecolteForBatch?.id === id) {
    dataStore.selectedRecolteForBatch = null;
  } else {
    const found = dataStore.recoltes.find(r => r.id === id);
    dataStore.selectedRecolteForBatch = found ?? null;
  }
}

export const getMoisDisponibles = (t: (key: string) => string) => [
  { value: 1, label: t('months.january') },
  { value: 2, label: t('months.february') },
  { value: 3, label: t('months.march') },
  { value: 4, label: t('months.april') },
  { value: 5, label: t('months.may') },
  { value: 6, label: t('months.june') },
  { value: 7, label: t('months.july') },
  { value: 8, label: t('months.august') },
  { value: 9, label: t('months.september') },
  { value: 10, label: t('months.october') },
  { value: 11, label: t('months.november') },
  { value: 12, label: t('months.december') },
];

export const getAnneesDisponibles = () => [
  { value: 2024 },
  { value: 2025 },
  { value: 2026 },
];

export const filteredRecoltes = computed(() => {
  const dataStore = useDataStore();
  return dataStore.recoltes
    .filter(r => {
      const date = transformCodeToDate(r.daycode); // ex: "2025-04-15"
      if (!date) return false;
      const annee = parseInt(date.slice(0, 4));
      const mois = parseInt(date.slice(5, 7));

      return (
        (!dataStore.filtreEspece || r.specie?.id === dataStore.filtreEspece) &&
        (!dataStore.filtreAnnee || annee === Number(dataStore.filtreAnnee)) &&
        (!dataStore.filtreMois || mois === dataStore.filtreMois)
      );
    })
    .sort((a, b) => {
      const dateA = transformCodeToDate(a.daycode); // ex: "2025-04-15"
      const dateB = transformCodeToDate(b.daycode);

      // Tri par date croissante (du plus ancien au plus récent)
      return new Date(dateA).getTime() - new Date(dateB).getTime();
    });
});

export function isRecolteSpecieMi175(specie: number): boolean {
  return (
    specie === 6
  );
}

export function isRecolteSpecieMi136(specie: number): boolean {
  return (
    specie === 5
  );
}